/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { AuthService } from 'app/um/services/auth-service';
import { LookupService } from 'app/cd/services/lookup-service';
import { Toaster } from 'app/shared/lib/toaster';
import { IApiResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { IConfirmationDialogResult, confirmation } from 'app/shared/elements/ess-confirm';
import { fireEvents } from 'app/shared/lib/fire-events';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { AuthReloadEvent, IAuthChangeCustomEventDetail } from 'app/um/lib/events';
import { I18N } from 'aurelia-i18n';
import { guid } from 'app/shared/lib/guid';
import { getReference } from 'app/shared/models/essette-types';
import { deepClone } from 'app/shared/lib/utilities';
import { IPopup } from 'app/shared/elements/popup-base';
import { IExtIqcSettings } from 'app/ext/iqc/ext-iqc';
import { IqcMedicalReviewsGrid } from 'app/ext/iqc/iqc-medical-reviews-grid';
import { OpenFlags } from 'app/shared/flags/open-flags';
import * as CD from 'app/cd/models/essette-api';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class DiagnosisGrid extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    @bindable.boolean()
    public readonly: boolean = true;

    @bindable.objectNull({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reload' })
    public auth: UM.Auth | null = null;

    @bindable.boolean()
    public showExternalActions: boolean = true;

    @bindable.boolean()
    public showOpenIqcSummary = false;

    @bindable.boolean()
    public flagsVisible: boolean = true;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected pageSize: number | undefined = 10;

    protected diagnosisExtIqcSettings: IExtIqcSettings = {
        allowOpen: true,
        showOpenBookView: false,
        showOpenReviewSummaryPdf: true,
        allowReviewCopy: true
    };

    protected iqcModalExtIqcSettings: IExtIqcSettings = {
        allowOpen: false,
        showOpenBookView: false,
        showOpenReviewSummaryPdf: true,
        allowReviewCopy: true
    };

    private diagnoses: UM.AuthDiagnosis[] = [];
    protected commonDiagnoses: UM.CommonDiagnosis[] = [];
    public diagnosisSearchQuery: string | null = null;          // the value of the autocomplete text box
    protected diagnosisSearchResults: CD.Diagnosis[] = [];     // array of search results from API for autocomplete (not currently used)
    private sorting = new Sorting();
    private defaultSorting = new Sorting();
    protected resultCount: number = 0;
    protected includeInactive = false;

    protected selectedDiagnosis: UM.AuthDiagnosis | null = null;

    private authReloaded: Subscription | null = null;
    private eventSubscriptions: Array<Subscription> = [];

    protected hasAddCommon: boolean = false;
    protected usePlaceholder: boolean = false
    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $saveDiagnosisModal!: IPopup;
    protected $iqcModal!: IPopup;
    protected $iqcMedicalReviewsGrid!: IqcMedicalReviewsGrid;

    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly authService: AuthService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        private readonly $element: Element,
        private readonly events: EventAggregator,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);

        this.defaultSorting.entries = [
            {
                reference: 'isPrimary',
                direction: SortDirection.Descending
            },
            {
                reference: 'definition.code',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        if(!this.auth) {
            throw new Error('Auth missing');
        }

        this.checkPermissions();

        this.loadDiagnoses();
        this.loadCommonDiagnoses();

        this.authReloaded = this.events.subscribe(AuthReloadEvent, (e: AuthReloadEvent) => {
            this.loadDiagnoses();
            this.loadCommonDiagnoses();
        });
    }

    protected unbind(): void {
        if(this.authReloaded) {
            this.authReloaded.dispose();
        }
        this.eventSubscriptions.forEach((s) => s.dispose());
        this.eventSubscriptions = [];
    }

    ////****************** Events ***************************************************////
    protected async searchDiagnoses(value: string): Promise<void> {
        if(!value) {
            if(this.diagnosisSearchResults) this.diagnosisSearchResults = [];
            return;
        }
        if(value.length < 2) {
            return;
        }

        let authDate: string | null = null;
        if(this.auth && this.auth.serviceDate?.dateRange?.start) {
            authDate = this.auth.serviceDate.dateRange.start;
        }
        if(!authDate && this.auth && this.auth.requestedDateTime) {
            authDate = this.auth.requestedDateTime;
        }
        if(!authDate) {
            throw new Error('Missing auth date to search diagnoses.');
        }

        const fields = ['code', 'description'];

        try {
            this.state.beginLoad();
            const response = await this.lookupService.searchDiagnoses(value, authDate, { top: 30 }, [], fields);
            this.diagnosisSearchResults = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    protected async selectDiagnosis(diagnosis: CD.Diagnosis): Promise<void> {
        if(!this.auth || !diagnosis) { return; }
        const saveAuthDiagnosis: UM.AuthDiagnosis = {
            auth: getReference(this.auth),
            definition: getReference(diagnosis)
        };

        const response = await this.addAuthDiagnosis(saveAuthDiagnosis, diagnosis?.code);
        if(response.isSuccessful) {
            this.diagnosisSearchQuery = null;
            this.diagnosisSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
        }
    }

    protected readonlyChanged(): void {
        this.checkPermissions();
        this.loadCommonDiagnoses();
    }

    protected openIqcSummaryModal(diagnosis: UM.AuthDiagnosis): void {
        if(!this.$iqcModal || !this.$iqcMedicalReviewsGrid || !diagnosis.id) return;
        this.$iqcMedicalReviewsGrid.diagnosis = diagnosis;
        this.selectedDiagnosis = deepClone(diagnosis);
        this.$iqcModal.open();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        if(this.readonly) return;
        this.accountContext.load();
        this.hasAddCommon = this.accountContext.hasPermission('UM.CommonDiagnosis.Query');
    }

    private async reload(): Promise<void> {
        await this.loadDiagnoses();

        if(this.$saveDiagnosisModal?.state == OpenFlags.Open) {
            const saveDiagnosis: UM.AuthDiagnosis | undefined = this.diagnoses.find((s: UM.AuthService) => s.id == this.selectedDiagnosis?.id);
            if(saveDiagnosis) {
                this.selectedDiagnosis = deepClone(saveDiagnosis);
            }
        }
    }

    protected editDiagnosis(diagnosis: UM.AuthDiagnosis): void {
        if(!diagnosis || !this.$saveDiagnosisModal) return;

        this.selectedDiagnosis = deepClone(diagnosis);

        this.$saveDiagnosisModal.open();
    }

    protected async applyDiagnosisSort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.loadDiagnoses();
    }

    protected filterDiagnoses = (diagnosis: CD.Diagnosis): boolean => {
        if(!this.diagnoses && !diagnosis) return false;
        return !this.diagnoses.some(d => d.$state === 'active' && d.definition?.id === diagnosis.id);
    }

    protected filterCommonDiagnoses = (diag: UM.CommonDiagnosis): boolean => {
        if(this.diagnoses && !diag?.diagnosis) return false;
        return !this.diagnoses.some(d => d.$state === 'active' && d.definition?.id === diag.diagnosis!.id);
    }

    protected async addCommonDiagnosis(diagnosis: UM.CommonDiagnosis): Promise<void> {
        if(!this.auth || !diagnosis) return;
        const saveAuthDiagnosis: UM.AuthDiagnosis = {
            auth: getReference(this.auth),
            definition: getReference(diagnosis)
        };

        await this.addAuthDiagnosis(saveAuthDiagnosis, diagnosis?.diagnosis?.code);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadDiagnoses(): Promise<void> {
        if(!this.auth?.id) {
            this.diagnoses = [];
            return;
        }

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: this.includeInactive,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting.entries.length ? this.sorting : this.defaultSorting
        };

        const includes = [
            'definition',
            'flags'
        ];

        const fields = [
            'definition.code',
            'definition.description',
            'flags.description',
            'isPrimary'
        ];

        if(!this.readonly) {
            includes.push('definition.type');
            fields.push('definition.type.dateRange.end', 'definition.type.dateRange.start');
        }

        // include if IQC enabled
        if(this.env.config.integrations.includes('iqc') && (!this.auth?.externalSystem || this.auth.externalSystem.code == 'IQC') && this.showExternalActions) {
            const iqcIncludes = [
                'medicalReview',
                'medicalReview.content'
            ];
            includes.push(...iqcIncludes);
            fields.push('medicalReview.*');
        }

        // include if CWQI enabled
        if(this.env.config.integrations.includes('cwqi') && (!this.auth?.externalSystem || this.auth.externalSystem.code == 'CWQI') && this.showExternalActions) {
            includes.push('definition.type');
            fields.push('definition.type.code');
        }

        if(this.includeInactive) {
            includes.push('metadata.activeChanged.byUser');
            fields.push('metadata.activeChanged.byUser.name.full', 'metadata.activeChanged.deactivatedReason', 'metadata.activeChanged.timestamp');
        }

        try {
            this.state.beginLoad();

            const response = await this.authService.loadDiagnosesByAuth(this.auth.id, options, includes, fields);
            this.diagnoses = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            // Check if diagnoses contain an iqc auth medical review for hidden placeholder icon in non medical review diagnoses to retain alignment of button placement for all rows.
            this.usePlaceholder = !this.readonly && this.env.config.integrations.includes('iqc') && !!this.diagnoses.find(d => !!d.medicalReview);

            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCommonDiagnoses(): Promise<void> {
        if(this.readonly || !this.hasAddCommon) return;

        const fields = [
            'diagnosis.code',
            'diagnosis.description'
        ];

        try {
            this.state.beginLoad();
            const response = await this.authService.loadCommonDiagnoses({ all: true }, fields);
            this.commonDiagnoses = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async addAuthDiagnosis(authDiagnosis: UM.AuthDiagnosis, diagnosisCode: string | null = null): Promise<IApiResults> {
        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.authService.insertAuthDiagnosis(authDiagnosis);
            isSuccessful = response.isSuccessful;
            await this.loadDiagnoses();
            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: true };
            fireEvents(this.$element, ['auth-change'], detail);
            return response;
        } catch(error) {
            throw error;
        } finally {
            const message = `${this.i18n.tr('um:auth.diagnosis.code.$long')} ${diagnosisCode}`;
            if(isSuccessful) this.toaster.info().message(this.i18n.tr('ui:messages.events.added', { what: message })).show();
            else this.toaster.error().message(this.i18n.tr('ui:messages.events.addFailed', { what: message })).show();
            this.state.endSave();
        }
    }

    @apiCall
    protected async deleteDiagnosis(diagnosisId: string): Promise<void> {
        if(!this.auth) return;

        const result: IConfirmationDialogResult = await confirmation.showDelete('um:auth.diagnosis.$', 1, true);
        if(!(result.dialogResponse === confirmation.options.OK && !!result.reason)) return;

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.authService.activateAuthDiagnosis(diagnosisId, false, result.reason);
            isSuccessful = response.isSuccessful;
            await this.loadDiagnoses();
            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: true };
            fireEvents(this.$element, ['auth-change'], detail);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.deleted('um:auth.diagnosis.$', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    public async updatePrincipal(diagnosis: UM.AuthDiagnosis, isPrimary: boolean) {
        if(!diagnosis) return;

        const saveAuthDiagnosis: UM.AuthDiagnosis = {
            id: diagnosis.id,
            isPrimary: isPrimary
        };

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.authService.updateAuthDiagnosisExclusive(saveAuthDiagnosis);
            isSuccessful = response.isSuccessful;
            await this.loadDiagnoses();
            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: false };
            fireEvents(this.$element, ['auth-change'], detail);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('um:auth.diagnosis.$', isSuccessful);
            this.state.endSave();
        }
    }
}
